import { StateCreator } from 'zustand';

import { createAppStore } from '../zustand';
import {
  GetWebPromptDetailsInfoArgs,
  WebPromptDetailsGlobalStoreDataType,
  WebPromptDetailsGlobalStoreType,
} from './webPromptDetailsGlobalStore.types';

const inFlightWebPromptDetailsRequests = new Map<string, Promise<WebPromptDetailsGlobalStoreDataType>>();

export const webPromptDetailsGlobalStoreInitialState: WebPromptDetailsGlobalStoreDataType = {
  webPromptDetails: null,
  isWebPromptDetailsSaved: false,
  hasFetchedWebPromptDetails: false,
};

const webPromptDetailsGlobalStore: StateCreator<WebPromptDetailsGlobalStoreType> = set => ({
  ...webPromptDetailsGlobalStoreInitialState,

  setWebPromptDetailsInfo: (value?: Partial<WebPromptDetailsGlobalStoreDataType>) => {
    set(prev => {
      const resolvedDetails = value?.webPromptDetails ?? prev.webPromptDetails;
      return {
        ...prev,
        ...value,
        webPromptDetails: resolvedDetails,
        isWebPromptDetailsSaved: !!resolvedDetails,
      };
    });
  },

  resetWebPromptDetailsInfo: () => {
    set({ ...webPromptDetailsGlobalStoreInitialState });
  },
});

export const useWebPromptDetailsGlobalStore = createAppStore(webPromptDetailsGlobalStore, {
  usePersistentStore: false,
  persistentStoreName: 'WebPromptDetailsGlobalStore',
});

export const getWebPromptDetailsInfo = async ({
  customerId,
  locale = 'en-US',
  forceFetch = false,
  fetcher,
}: GetWebPromptDetailsInfoArgs): Promise<WebPromptDetailsGlobalStoreDataType> => {
  const state = useWebPromptDetailsGlobalStore.getState();

  if (!customerId) {
    return state;
  }

  const hasCustomerScopedCache =
    state.isWebPromptDetailsSaved && state.customerId === customerId && !!state.webPromptDetails;

  const hasFetchedForCustomer = state.hasFetchedWebPromptDetails && state.customerId === customerId;

  const inFlightRequest = inFlightWebPromptDetailsRequests.get(customerId);
  if (!forceFetch && inFlightRequest) {
    return inFlightRequest;
  }

  if (!forceFetch && (hasCustomerScopedCache || hasFetchedForCustomer)) {
    return state;
  }

  const requestPromise = (async () => {
    try {
      if (!fetcher) {
        return state;
      }

      const data = await fetcher({ customerId, locale });

      const { setWebPromptDetailsInfo } = useWebPromptDetailsGlobalStore.getState();
      setWebPromptDetailsInfo({
        webPromptDetails: data ?? null,
        customerId,
        hasFetchedWebPromptDetails: true,
        error: undefined,
      });

      return useWebPromptDetailsGlobalStore.getState();
    } catch (error) {
      const { setWebPromptDetailsInfo } = useWebPromptDetailsGlobalStore.getState();
      setWebPromptDetailsInfo({
        customerId,
        hasFetchedWebPromptDetails: true,
        error,
      });

      throw error;
    } finally {
      inFlightWebPromptDetailsRequests.delete(customerId);
    }
  })();

  if (!forceFetch) {
    inFlightWebPromptDetailsRequests.set(customerId, requestPromise);
  }

  return requestPromise;
};
